'use client'

// SUMMARY: Navbar search. Two things in one box: NAVIGATION (the product's own destinations, matched
// client-side so they are instant and always available) and CONTENT (chats, projects, documents, canvases
// and their contents, from /api/search). Opens as a dialog on click or Cmd/Ctrl+K.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useSession } from 'next-auth/react'
import {
	BarChart3,
	Boxes,
	Brain,
	CalendarDays,
	CheckSquare,
	CreditCard,
	Database,
	FileText,
	FolderKanban,
	Gauge,
	Gift,
	Library,
	Loader2,
	MessageSquare,
	NotebookPen,
	PenTool,
	Search,
	Server as ServerIcon,
	ShieldAlert,
	Stamp,
	Table as TableIcon,
	User,
	Workflow,
	type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { CANVAS_ENABLED, PROJECTS_ENABLED } from '@/lib/features'
import { normalizeTerm } from '@/lib/search/normalize'
import type { SearchResult, SearchResultKind } from '@/lib/search/query'

const DEBOUNCE_MS = 180
const MIN_QUERY_LENGTH = 2

// ===============================
// SECTION: NAVIGATION TARGETS
// ===============================

type NavTarget = { key: string; href: string; icon: LucideIcon; admin?: boolean }

// Labels come from dashboard.sidebar.* so this list can never drift from the sidebar + menu grid.
const NAV_TARGETS: NavTarget[] = [
	{ key: 'chat', href: '/dashboard/chat', icon: MessageSquare },
	...(PROJECTS_ENABLED ? [{ key: 'projects', href: '/dashboard/projects', icon: FolderKanban }] : []),
	...(CANVAS_ENABLED ? [{ key: 'canvas', href: '/dashboard/canvas', icon: PenTool }] : []),
	{ key: 'documents', href: '/dashboard/documents', icon: FileText },
	{ key: 'memory', href: '/dashboard/settings/memory', icon: Brain },
	{ key: 'branding', href: '/dashboard/settings/branding', icon: Stamp },
	{ key: 'billing', href: '/dashboard/settings/billing', icon: CreditCard },
	{ key: 'account', href: '/dashboard/settings/account', icon: User },
	{ key: 'freshChat', href: '/dashboard/chat/fresh', icon: Gauge, admin: true },
	{ key: 'analytics', href: '/dashboard/admin/analytics', icon: BarChart3, admin: true },
	{ key: 'flow', href: '/dashboard/admin/flow', icon: Workflow, admin: true },
	{ key: 'database', href: '/dashboard/admin/database', icon: Database, admin: true },
	{ key: 'security', href: '/dashboard/admin/security', icon: ShieldAlert, admin: true },
	{ key: 'access', href: '/dashboard/admin/access', icon: Gift, admin: true },
	{ key: 'servers', href: '/dashboard/admin/servers', icon: ServerIcon, admin: true },
	{ key: 'models', href: '/dashboard/admin/models', icon: Boxes, admin: true },
	{ key: 'registry', href: '/dashboard/admin/registry', icon: Library, admin: true },
]

const KIND_ICON: Record<SearchResultKind, LucideIcon> = {
	conversation: MessageSquare,
	message: MessageSquare,
	project: FolderKanban,
	note: NotebookPen,
	table: TableIcon,
	todo: CheckSquare,
	event: CalendarDays,
	document: FileText,
	canvas: PenTool,
}

type Row =
	| { type: 'nav'; id: string; label: string; href: string; icon: LucideIcon }
	| { type: 'result'; id: string; result: SearchResult }

// ===============================
// SECTION: PLATFORM MODIFIER KEY
// ===============================

/**
 * The shortcut is Cmd on macOS and Ctrl everywhere else, and German keyboards label that key "Strg" - so
 * the badge has to reflect BOTH the platform and the UI language, or it tells half the users the wrong key.
 */
function readIsMac(): boolean {
	// userAgentData.platform is the non-deprecated source where it exists (Chromium); navigator.platform is
	// still the only thing Safari and Firefox offer.
	const platform =
		(navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
		navigator.platform ??
		''
	return /mac/i.test(platform)
}

// Stable no-op: the platform cannot change during a session, so there is nothing to subscribe to. Reading
// it through useSyncExternalStore (rather than an effect) keeps the server snapshot explicit, so the
// non-Mac label renders on the server and is corrected on hydration without a mismatch warning.
const subscribeToPlatform = () => () => {}
const serverIsMac = () => false

// ===============================
// SECTION: COMPONENT
// ===============================

export function NavSearch() {
	const t = useTranslations('nav.search')
	const tSidebar = useTranslations('dashboard.sidebar')
	const locale = useLocale()
	const isMac = useSyncExternalStore(subscribeToPlatform, readIsMac, serverIsMac)
	const modifierKey = isMac ? '⌘' : locale === 'de' ? 'Strg' : 'Ctrl'
	const router = useRouter()
	const { data: session } = useSession()
	const isAdmin = session?.user?.isAdmin ?? false

	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [results, setResults] = useState<SearchResult[]>([])
	const [loading, setLoading] = useState(false)
	const [active, setActive] = useState(0)
	const inputRef = useRef<HTMLInputElement>(null)
	// Guards against an earlier, slower response overwriting a newer one.
	const requestRef = useRef(0)

	// Cmd/Ctrl+K from anywhere.
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
				event.preventDefault()
				setOpen(true)
			}
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [])

	// Debounced content search. Navigation matching is local, so it never waits on this. Every state write
	// happens inside the timer/promise rather than in the effect body - a synchronous setState here would
	// cascade an extra render on every keystroke.
	const trimmedQuery = query.trim()
	const isSearchable = trimmedQuery.length >= MIN_QUERY_LENGTH
	useEffect(() => {
		if (!isSearchable) return
		const id = ++requestRef.current
		const timer = setTimeout(() => {
			setLoading(true)
			fetch(`/api/search?q=${encodeURIComponent(trimmedQuery)}`)
				.then((res) => (res.ok ? res.json() : { results: [] }))
				.then((data: { results?: SearchResult[] }) => {
					if (id !== requestRef.current) return
					setResults(data.results ?? [])
					setLoading(false)
				})
				.catch(() => {
					if (id === requestRef.current) setLoading(false)
				})
		}, DEBOUNCE_MS)
		return () => clearTimeout(timer)
	}, [trimmedQuery, isSearchable])

	const navRows = useMemo<Row[]>(() => {
		const needle = normalizeTerm(query.trim())
		if (!needle) return []
		return NAV_TARGETS.filter((target) => !target.admin || isAdmin)
			.map((target) => ({ target, label: tSidebar(target.key) }))
			.filter(({ label }) => normalizeTerm(label).includes(needle))
			.slice(0, 5)
			.map(({ target, label }) => ({
				type: 'nav' as const,
				id: `nav:${target.key}`,
				label,
				href: target.href,
				icon: target.icon,
			}))
	}, [query, isAdmin, tSidebar])

	const rows = useMemo<Row[]>(
		() => [
			...navRows,
			// Results belong to whatever query produced them; a query below the threshold shows none, without
			// needing an effect to clear the state.
			...(isSearchable ? results : []).map((result) => ({
				type: 'result' as const,
				id: `${result.kind}:${result.id}`,
				result,
			})),
		],
		[navRows, results, isSearchable]
	)

	// Clamped rather than reset in an effect: the list shrinks as results arrive, and the highlight must
	// simply stay inside it.
	const activeIndex = Math.min(active, Math.max(0, rows.length - 1))

	const go = useCallback(
		(row: Row) => {
			setOpen(false)
			setQuery('')
			router.push(row.type === 'nav' ? row.href : row.result.href)
		},
		[router]
	)

	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setActive((index) => Math.min(index + 1, rows.length - 1))
		} else if (event.key === 'ArrowUp') {
			event.preventDefault()
			setActive((index) => Math.max(index - 1, 0))
		} else if (event.key === 'Enter' && rows[activeIndex]) {
			event.preventDefault()
			go(rows[activeIndex])
		}
	}

	const showEmpty = isSearchable && !loading && rows.length === 0

	return (
		<>
			{/* The navbar affordance is a button, not a live input: the real field lives in the dialog, so the
			    header stays compact and the same entry point works on touch, mouse and Cmd+K. */}
			<button
				type="button"
				onClick={() => setOpen(true)}
				aria-label={t('open')}
				className="flex h-8 items-center gap-2 rounded-lg border border-border/60 bg-background/60 px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:w-44 lg:w-56"
			>
				<Search className="h-4 w-4 shrink-0" />
				<span className="hidden truncate sm:inline">{t('placeholder')}</span>
				<kbd className="ml-auto hidden h-5 shrink-0 items-center gap-0.5 rounded border border-border/60 px-1.5 font-sans text-[11px] leading-none font-medium lg:inline-flex">
					<span className="leading-none">{modifierKey}</span>K
				</kbd>
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent
					showCloseButton={false}
					className="top-[12%] max-w-[min(calc(100vw-2rem),40rem)] translate-y-0 gap-0 overflow-hidden p-0"
				>
					<DialogTitle className="sr-only">{t('open')}</DialogTitle>
					<div className="flex items-center gap-2 border-b px-3">
						<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
						<input
							ref={inputRef}
							autoFocus
							value={query}
							onChange={(event) => {
								setQuery(event.target.value)
								setActive(0)
							}}
							onKeyDown={onKeyDown}
							placeholder={t('placeholder')}
							className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
						/>
						{loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
					</div>

					<div className="max-h-[60dvh] overflow-y-auto p-1.5">
						{!isSearchable && <p className="px-3 py-6 text-center text-xs text-muted-foreground">{t('hint')}</p>}
						{showEmpty && <p className="px-3 py-6 text-center text-xs text-muted-foreground">{t('noResults')}</p>}

						{rows.map((row, index) => {
							const Icon = row.type === 'nav' ? row.icon : KIND_ICON[row.result.kind]
							const title = row.type === 'nav' ? row.label : row.result.title
							const snippet = row.type === 'result' ? row.result.snippet : undefined
							const context = row.type === 'result' ? row.result.context : undefined
							return (
								<button
									key={row.id}
									type="button"
									onMouseEnter={() => setActive(index)}
									onClick={() => go(row)}
									className={cn(
										'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
										index === activeIndex ? 'bg-accent text-foreground' : 'text-muted-foreground'
									)}
								>
									<Icon
										className={cn(
											'mt-0.5 h-4 w-4 shrink-0',
											row.type === 'nav' ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'
										)}
									/>
									<span className="min-w-0 flex-1">
										<span className="block truncate text-sm font-medium text-foreground">{title}</span>
										{snippet && <span className="block truncate text-xs text-muted-foreground">{snippet}</span>}
										{context && !snippet && (
											<span className="block truncate text-xs text-muted-foreground">{context}</span>
										)}
									</span>
									<span className="mt-0.5 shrink-0 text-[10px] tracking-wide text-muted-foreground uppercase">
										{row.type === 'nav' ? t('kinds.page') : t(`kinds.${row.result.kind}`)}
									</span>
								</button>
							)
						})}
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
